import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { OverLayParameter } from '../../App';
import { useTracking } from '../../tools/analytics';
import { RequirementsProps } from '../../tools/context';
import { useTranslation } from '../../tools/i18n';
import { OAuthButton } from '../OAuthButton';
import { Picto } from '../Picto';

export interface LoginPopupProps {
  close: () => void;
  requirements: RequirementsProps;
  coupon: any;
  context?: any;
  siteConfiguration: any;
  setOverlay: (p: OverLayParameter) => void;
}

export const LoginPopup = ({
  setOverlay,
  requirements,
  context,
  siteConfiguration,
}: LoginPopupProps) => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const [optin, setOptin] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    trackEvent('open_login_popin');
    // RGAA — déplacer le focus sur le bouton de fermeture à l'ouverture
    closeButtonRef.current?.focus();
  }, []);

  // RGAA — fermeture par la touche Échap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();

      // RGAA — piège de focus : garder le focus à l'intérieur de la modale
      if (e.key === 'Tab' && modalRef.current) {
        const focusableSelectors =
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const focusable = Array.from<HTMLElement>(
          modalRef.current.querySelectorAll(focusableSelectors)
        );
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  let redirect = new URL(window.location.href);

  if (context && context.redirect) {
    redirect = context.redirect;
  }
  if (context && context.queries) {
    Object.keys(context.queries).forEach((k) => {
      redirect.searchParams.append(k, context.queries[k]);
    });
  }

  const redirectUrl = (success) => {
    const red = new URL(redirect.href);
    if (success) {
      red.searchParams.append(success, 'true');
    }
    return `${red.protocol}//${red.host}${red.pathname}${red.search}`;
  };

  const url = `${requirements.config.clientUrl}connexion-client?redirect=${encodeURIComponent(
    redirectUrl('successLoginPhone')
  )}`;

  const close = () => setOverlay(null);

  return (
    // RGAA — backdrop cliquable pour fermer
    <div className="popup loginPopup" onClick={close} aria-hidden="true">
      {/* RGAA — modale accessible : role=dialog, aria-modal, aria-labelledby, focus trap */}
      <div
        className="popupBlock"
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-popup-title"
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        aria-hidden={undefined}
      >
        <button
          className="close"
          onClick={close}
          ref={closeButtonRef}
          aria-label="Fermer la fenêtre de connexion"
        >
          <Picto iconKey={'cross'} />
        </button>
        <div id="login-popup-title" className="title">
          {t('loginPopup.connect')}
        </div>
        <div className="optin">
          <input
            type="checkbox"
            id="optinBox"
            onChange={() => setOptin((p) => !p)}
            checked={optin}
          />
          <label htmlFor="optinBox">{t('loginPopup.optin')}</label>
        </div>
        <a
          href={url}
          className="Button client"
          onClick={() => {
            trackEvent('login_click');
          }}
        >
          {t('common.login')}
        </a>
        {siteConfiguration.oauthEnabled && (
          <OAuthButton
            redirectUrl={redirectUrl('successLoginGoogle')}
            requirements={requirements}
            provider={'google'}
            parameters={{
              customerId: requirements.config.customerId,
              optin: optin.toString(),
            }}
          />
        )}
        <div className="subtitle">{t('loginPopup.join')}</div>
        <a
          href={url}
          className="Button white"
          onClick={() => {
            trackEvent('signup_click');
          }}
        >
          {t('common.signup')}
        </a>
      </div>
    </div>
  );
};
